/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Menu, Icon, Button } from 'grab-ui';
import I18n from 'i18n-js';
import LogoV2 from 'public/images/logo-v2-header.svg';
import {
  SUPPORTED_COUNTRIES,
  COUNTRY_FLAGS_CIRCLE,
  getLocalesByCountryCode,
  getSelectedCountry,
  setSelectedCountry,
} from 'constants/locale-constants';
import { useLocales } from 'hooks/useLocales';

import { getGFBLoginLink, getGrabIDLoginLink } from 'endpoints/login-endpoints';

import FLAG_SG from 'public/images/flag-singapore.svg';
import FLAG_MY from 'public/images/flag-malaysia.svg';
import FLAG_PH from 'public/images/flag-philippines.svg';
import FLAG_ID from 'public/images/flag-indonesia.svg';
import FLAG_TH from 'public/images/flag-thailand.svg';
import FLAG_VN from 'public/images/flag-vietnam.svg';
import FLAG_KH from 'public/images/flag-cambodia.svg';

import styles from './login-section.less';

export const HEADER_ID = 'headerID';

const preloadImages = [
  LogoV2,
  FLAG_SG,
  FLAG_MY,
  FLAG_PH,
  FLAG_ID,
  FLAG_TH,
  FLAG_VN,
  FLAG_KH,
];

const CountriesDropdown = ({ selectedCountry }) => {
  const { setLocale } = useLocales();

  const handleSelectChangeCountry = useCallback(
    (countryCode, language) => () => {
      setSelectedCountry(countryCode);
      setLocale(language);
      window.location.reload();
    },
    [setLocale],
  );

  const overlay = useMemo(
    () => (
      <Menu>
        {SUPPORTED_COUNTRIES.map(({ code }) => {
          const languages = getLocalesByCountryCode(code);

          if (languages && languages.length > 1) {
            return (
              <Menu.SubMenu
                key={code}
                title={
                  <div className={styles.itemContainer}>
                    <div className={styles.flagContainer}>
                      <img
                        className={styles.flag}
                        src={COUNTRY_FLAGS_CIRCLE[code]}
                        alt="country flag"
                      />
                    </div>

                    <span
                      className={styles.dropdownTitle}
                      onClick={handleSelectChangeCountry(code, languages[0])}
                    >
                      {I18n.t(`orderSearch.country.${code}`)}
                    </span>
                  </div>
                }
              >
                {languages.map(language => (
                  <Menu.Item key={`${code}-${language}`}>
                    <div className={styles.itemContainer}>
                      <span
                        className={styles.dropdownTitle}
                        onClick={handleSelectChangeCountry(code, language)}
                      >
                        {I18n.t(`orderSearch.language.${language}`)}
                      </span>
                    </div>
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            );
          }

          return (
            <Menu.Item key={`${code}-${languages[0]}`}>
              <div className={styles.itemContainer}>
                <div className={styles.flagContainer}>
                  <img
                    className={styles.flag}
                    src={COUNTRY_FLAGS_CIRCLE[code]}
                    alt="country flag"
                  />
                </div>

                <span
                  className={styles.dropdownTitle}
                  onClick={handleSelectChangeCountry(code, languages[0])}
                >
                  {I18n.t(`orderSearch.country.${code}`)}
                </span>
              </div>
            </Menu.Item>
          );
        })}
      </Menu>
    ),
    [handleSelectChangeCountry],
  );

  return (
    <Dropdown overlay={overlay} trigger={['click']}>
      <Button className={styles.dropdownButton}>
        <div className={styles.countriesDropdownContainer}>
          <div className={styles.flagContainer}>
            <img
              className={styles.flag}
              src={COUNTRY_FLAGS_CIRCLE[selectedCountry]}
              alt="country flag"
            />
          </div>

          <span className={styles.title}>
            {I18n.t(`orderSearch.country.${selectedCountry}`)}
          </span>

          <Icon type="down" />
        </div>
      </Button>
    </Dropdown>
  );
};

CountriesDropdown.propTypes = {
  selectedCountry: PropTypes.string.isRequired,
};

const LoginButton = () => {
  const navigate = useNavigate();

  const handleLoginClick = useCallback(
    ({ key }) => {
      switch (key) {
        case 'grabId': {
          const link = getGrabIDLoginLink();
          window.location.href = link;
          break;
        }
        case 'gfb': {
          const link = getGFBLoginLink();
          window.location.href = link;
          break;
        }
        default:
          break;
      }
    },
    [navigate],
  );

  const overlay = useMemo(
    () => (
      <Menu onClick={handleLoginClick}>
        <Menu.Item key="grabId">
          <div className={styles.itemContainer}>
            {I18n.t('orderSearch.login.grabID')}
          </div>
        </Menu.Item>

        <Menu.Item key="gfb">
          <div className={styles.itemContainer}>
            {I18n.t('orderSearch.login.gfb')}
          </div>
        </Menu.Item>
      </Menu>
    ),
    [handleLoginClick],
  );
  return (
    <Dropdown overlay={overlay} trigger={['click']}>
      <Button className={styles.loginButton}>
        {I18n.t('orderSearch.login.button')}
      </Button>
    </Dropdown>
  );
};

const LoginSection = () => {
  const { getLocale } = useLocales();

  const [selectedCountry] = useState(() => getSelectedCountry());

  useEffect(
    () => {
      const preferedLanguage = getLocale();
      I18n.locale = preferedLanguage;
    },
    [getLocale],
  );

  const renderPreloadImages = useMemo(
    () =>
      preloadImages.map(i => (
        <img
          key={`flags-${i}`}
          style={{ display: 'none' }}
          src={i}
          alt={`preload-${i}`}
        />
      )),
    [],
    [],
  );

  return (
    <header className={styles.header}>
      <div className={styles.logoContainer}>
        <img className={styles.logo} src={LogoV2} alt="grab express" />
      </div>

      <div className={styles.actionContainer}>
        <CountriesDropdown selectedCountry={selectedCountry} />

        <LoginButton />
      </div>
      {renderPreloadImages}
    </header>
  );
};

export default LoginSection;
